import { ESTADOS_CUPO, LEYENDA_ESTADOS, ocupacionDeDoctor } from "./agendaCitas";
import { tituloClinico } from "./textoClinico";

/* ============================================================================
   RESUMEN DEL DÍA · cobertura y estados

   Recepción necesitaba saber, sin salir de la agenda, cuántas citas del día son
   particulares y cuántas las cubre un tercero, y cómo están repartidos los
   estados.

   NO PIDE NADA AL SERVIDOR. Se calcula en una sola pasada sobre el mismo
   arreglo que ya pinta la cuadrícula (`citasDoctotes`, de `POST doctores_cita`).
   Todos los campos que usa ya viajaban:

     · `pacientes.tipo_seguros.tipo`  -> particular o cubierto
     · `pacientes.seguros.nombre`     -> desglose por aseguradora
     · `estado_cita.idestado_cita`    -> reparto de estados

   DOS CUBOS, NO CUATRO. El catálogo real tiene PARTICULAR (1.290 pacientes),
   SEGUROS (1.416), EMPRESAS (38) y Convenio (0). La pregunta de caja no es cuál
   de los cuatro, sino QUIÉN PAGA: el paciente o un tercero. Así que se agrupa
   en `particular` y `cubierto`, que es además el mismo corte que ya hacen los
   chips de cobertura de las tarjetas.

   SE AGRUPA POR TEXTO, NUNCA POR ID. El catálogo `tipo_seguro` tiene ids
   duplicados: 1 y 6 son ambos "PARTICULAR", 2 y 7 son ambos "EMPRESAS".
   Agrupar por id partiría a los particulares en dos montones.
   ========================================================================== */

/** Normaliza el tipo para compararlo: sin espacios sobrantes y en mayúsculas. */
function normalizar(texto) {
    return String(texto ?? "").trim().toLocaleUpperCase("es");
}

/**
 * ¿La paga el paciente o un tercero?
 *
 * Devuelve `null` cuando la cita no trae tipo de cobertura. No se supone
 * "particular" por omisión: en la base no ocurre (0 de 2.756 citas activas),
 * pero si ocurriera, un hueco es más honesto que un dato inventado -- y el
 * resumen lo delata, porque entonces las partes no sumarían el total.
 */
export function cuboDeCobertura(cita) {
    const tipo = normalizar(cita?.pacientes?.tipo_seguros?.tipo);
    if (!tipo) return null;
    return tipo === "PARTICULAR" ? "particular" : "cubierto";
}

/**
 * El estado que pide gestión, que es el que merece salir en la etiqueta.
 *
 * Prioridad acordada: Sin confirmar (1) -> Reagendado (4) -> Confirmada (2).
 * "No asistió" NO existe en este sistema; el catálogo real son siete estados
 * (más "Eliminada", que el endpoint nunca devuelve). Reagendado ocupa el hueco
 * porque es el otro que exige llamar al paciente.
 */
export const PRIORIDAD_DESTACADO = [1, 4, 2];

/**
 * Cuenta el día entero en una sola pasada.
 *
 * @param {Array} citas el mismo arreglo que pinta la cuadrícula
 * @returns {{total, particular, cubierto, sinCobertura, aseguradoras, estados, destacado}}
 */
export function resumirCitas(citas) {
    const lista = Array.isArray(citas) ? citas : [];

    const porEstado = new Map();
    const porAseguradora = new Map();
    let particular = 0;
    let cubierto = 0;
    let sinCobertura = 0;

    for (const cita of lista) {
        const cubo = cuboDeCobertura(cita);
        if (cubo === "particular") particular += 1;
        else if (cubo === "cubierto") cubierto += 1;
        else sinCobertura += 1;

        /* El desglose por aseguradora es sólo de lo CUBIERTO: un particular con
           un registro de seguro llamado "PARTICULAR" -- que los hay en la base --
           no es una aseguradora. */
        if (cubo === "cubierto") {
            const nombre = String(cita?.pacientes?.seguros?.nombre ?? "").trim();
            const clave = nombre || "Sin aseguradora";
            porAseguradora.set(clave, (porAseguradora.get(clave) ?? 0) + 1);
        }

        const idEstado = Number(cita?.estado_cita?.idestado_cita);
        if (Number.isFinite(idEstado) && idEstado > 0) {
            porEstado.set(idEstado, (porEstado.get(idEstado) ?? 0) + 1);
        }
    }

    return armarResumen({
        total: lista.length,
        particular,
        cubierto,
        sinCobertura,
        porEstado: Object.fromEntries(porEstado),
        aseguradoras: Object.fromEntries(porAseguradora),
    });
}

/**
 * Da forma al resumen a partir de CONTEOS ya hechos -- vengan de contar las
 * citas del front (`resumirCitas`) o del backend (`normalizarResumenDia`).
 * Un solo sitio decide el orden de los estados, sus colores y el destacado.
 */
function armarResumen({ total, particular, cubierto, sinCobertura, porEstado, aseguradoras }) {
    /* Los estados salen en el orden de la LEYENDA del pie de la agenda -- el
       orden del flujo, no el alfabético ni el del id -- y con SUS mismas
       claves de color: no puede haber dos códigos distintos para el mismo
       estado en la misma pantalla. */
    const estados = LEYENDA_ESTADOS.map((estado) => ({
        id: estado.id,
        clave: estado.clave,
        etiqueta: estado.etiqueta,
        n: Number(porEstado?.[estado.id] ?? porEstado?.[String(estado.id)] ?? 0),
    }));

    const listaAseguradoras = Object.entries(aseguradoras ?? {})
        .map(([nombre, n]) => ({ nombre: tituloClinico(nombre), n: Number(n) }))
        // De mayor a menor; a igualdad, alfabético, para que no bailen entre días.
        .sort((a, b) => b.n - a.n || a.nombre.localeCompare(b.nombre, "es"));

    const destacado =
        PRIORIDAD_DESTACADO.map((id) => estados.find((e) => e.id === id))
            .find((e) => e && e.n > 0) ?? null;

    return {
        total: Number(total) || 0,
        particular: Number(particular) || 0,
        cubierto: Number(cubierto) || 0,
        // Si esto no es 0, las partes no suman el total y hay que decirlo.
        sinCobertura: Number(sinCobertura) || 0,
        aseguradoras: listaAseguradoras,
        estados,
        destacado,
    };
}

/**
 * El resumen del DÍA COMPLETO, tal como lo devuelve
 * `GET /citas/conteo-especialidad` en `resumenDia` (ver `FCitasConteo.ts`).
 *
 * El backend ya contó -- particular, cubierto, aseguradoras, estados por id --
 * porque el front sólo tiene en memoria una especialidad. Aquí sólo se le da
 * la forma que espera la etiqueta, con la MISMA función que el conteo local:
 * el color y el orden de los estados se deciden en un único sitio.
 *
 * `null` cuando el backend no lo mandó (versión antigua o fallo): la etiqueta
 * entonces no se muestra, que es mejor que un número a medias.
 */
export function normalizarResumenDia(resumenDia) {
    if (!resumenDia || typeof resumenDia !== "object") return null;
    return armarResumen({
        total: resumenDia.total,
        particular: resumenDia.particular,
        cubierto: resumenDia.cubierto,
        sinCobertura: resumenDia.sinCobertura,
        porEstado: resumenDia.porEstado ?? {},
        aseguradoras: normalizarAseguradoras(resumenDia.aseguradoras),
    });
}

/** El backend manda `[{nombre, n}]`; `armarResumen` quiere `{nombre: n}`. */
function normalizarAseguradoras(lista) {
    const mapa = {};
    for (const fila of Array.isArray(lista) ? lista : []) {
        if (fila?.nombre) mapa[fila.nombre] = Number(fila.n) || 0;
    }
    return mapa;
}

/**
 * Cupos del día: ocupados y totales.
 *
 * Reutiliza `ocupacionDeDoctor`, que es exactamente la misma cuenta que ya
 * muestra cada médico en su cabecera ("4/13"): las franjas DENTRO de su
 * horario, no todas las de la cuadrícula. Sumarlas da el total del día sin
 * ninguna llamada nueva.
 *
 * Devuelve `null` si falta el dato para calcularlo, para poder omitir la línea
 * en vez de enseñar una aproximación.
 */
export function contarCupos(doctores, franjas, citas, fecha) {
    if (!Array.isArray(doctores) || doctores.length === 0) return null;
    if (!Array.isArray(franjas) || franjas.length === 0) return null;

    let ocupados = 0;
    let totales = 0;
    for (const doctor of doctores) {
        const ocupacion = ocupacionDeDoctor(doctor, franjas, citas, fecha);
        if (!ocupacion) continue; // no atiende ese día
        ocupados += ocupacion.ocupadas;
        totales += ocupacion.total;
    }
    if (totales === 0) return null;
    return { ocupados, totales, libres: totales - ocupados };
}

/** Porcentaje entero de una parte sobre el total, sin dividir por cero. */
export function porcentaje(parte, total) {
    if (!total) return 0;
    return Math.round((parte / total) * 100);
}

/* ---------------------------------------------------------------------------
   EL FILTRO DE COBERTURA

   El filtro por ESTADO ya existía (`estadosVisibles` + el botón Filtros), así
   que se reutiliza tal cual. El de cobertura es lo único nuevo, y sigue la
   misma regla: ATENÚA, no oculta. Un cupo ocupado que desapareciera se vería
   libre y alguien agendaría encima.

   Un solo filtro de cobertura a la vez, en tres sabores:
     { tipo: "particular" }
     { tipo: "cubierto" }
     { tipo: "aseguradora", valor: "Humalab" }
   --------------------------------------------------------------------------- */

/** ¿Esta cita entra en el filtro de cobertura? Sin filtro, todas entran. */
export function citaCoincideCobertura(cita, filtro) {
    if (!filtro?.tipo) return true;
    const cubo = cuboDeCobertura(cita);
    if (filtro.tipo === "particular") return cubo === "particular";
    if (filtro.tipo === "cubierto") return cubo === "cubierto";
    if (filtro.tipo === "aseguradora") {
        // Se compara contra el mismo nombre presentado que se pintó en la fila.
        if (cubo !== "cubierto") return false;
        const nombre = tituloClinico(String(cita?.pacientes?.seguros?.nombre ?? "").trim() || "Sin aseguradora");
        return nombre === filtro.valor;
    }
    return true;
}

/** Cuántas citas del día caen dentro del filtro, para el contador del chip. */
export function contarCoincidencias(citas, filtro) {
    if (!filtro?.tipo) return 0;
    return (Array.isArray(citas) ? citas : []).filter((c) => citaCoincideCobertura(c, filtro)).length;
}

/** La etiqueta legible de un estado, por si hace falta fuera del resumen. */
export function etiquetaEstado(id) {
    return ESTADOS_CUPO[Number(id)]?.etiqueta ?? "Sin estado";
}
